import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, getDocs, collection } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, AuthState, UserRole } from '../types';
import { logAuditEvent } from '../hooks/useAuditLog';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  createUser: (email: string, password: string, role: UserRole, displayName?: string) => Promise<void>;
  getAllUsers: () => Promise<User[]>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert Firebase user to our User type
  const convertFirebaseUser = async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      // Try to get user document with retry logic
      let userDoc;
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          break;
        }
        // Wait a bit and retry (in case of race condition)
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
      }
      
      if (userDoc && userDoc.exists()) {
        const userData = userDoc.data();
        console.log('convertFirebaseUser: Found user in Firestore:', userData);
        console.log('convertFirebaseUser: User role from Firestore:', userData.role);
        console.log('convertFirebaseUser: User data keys:', Object.keys(userData));
        console.log('convertFirebaseUser: User data values:', Object.values(userData));
        
        // Handle case where role might be undefined or null
        const userRole: UserRole = userData.role || 'viewer';
        console.log('convertFirebaseUser: Using role:', userRole);

        // Self-heal partial documents. Older logins could create a doc holding only
        // { lastLogin } when the profile bootstrap failed - backfill identity fields.
        if (!userData.email || !userData.role || !userData.createdAt) {
          const heal: Record<string, any> = {};
          if (!userData.email) heal.email = firebaseUser.email;
          if (!userData.role) heal.role = userRole;
          if (!userData.createdAt) heal.createdAt = serverTimestamp();
          if (!userData.displayName && firebaseUser.displayName) heal.displayName = firebaseUser.displayName;
          try {
            await setDoc(doc(db, 'users', firebaseUser.uid), heal, { merge: true });
            console.log('convertFirebaseUser: Backfilled missing profile fields:', Object.keys(heal));
          } catch (healErr) {
            console.warn('convertFirebaseUser: Failed to backfill profile fields:', healErr);
          }
        }

        const convertedUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || userData.displayName || undefined,
          role: userRole as UserRole,
          lastLogin: userData.lastLogin?.toDate(),
          createdAt: userData.createdAt?.toDate() || new Date()
        };
        
        console.log('convertFirebaseUser: Converted user object:', convertedUser);
        return convertedUser;
      } else {
        console.log('convertFirebaseUser: User document not found in Firestore after retries');
        console.log('convertFirebaseUser: User ID:', firebaseUser.uid);
        console.log('convertFirebaseUser: User email:', firebaseUser.email);
        
        // Only create default user if this is truly a new user (not created through our createUser function)
        // Check if this is the first user (should be admin). Listing the users collection
        // is admin-only under the security rules, so a denied read just means "not first".
        let isFirstUser = false;
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          isFirstUser = usersSnapshot.empty;
        } catch {
          isFirstUser = false;
        }
        console.log('convertFirebaseUser: Is first user?', isFirstUser);
        
        // If user doesn't exist in Firestore, create account
        // First user gets admin role, others get reviewer role
        const defaultUser: User = {
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || undefined,
          role: isFirstUser ? 'admin' : 'viewer',
          lastLogin: new Date(),
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'users', firebaseUser.uid), {
          ...defaultUser,
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp()
        });
        
        console.log(`Created ${isFirstUser ? 'admin' : 'viewer'} user:`, firebaseUser.email);
        return defaultUser;
      }
    } catch (err) {
      console.error('Error converting Firebase user:', err);
      return null;
    }
  };

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      console.log('AuthContext: Attempting login for:', email);
      console.log('AuthContext: Firebase auth object:', auth);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      console.log('AuthContext: Login successful, user ID:', firebaseUser.uid);
      
      // Update last login time (include email so a merge-created doc is identifiable)
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        lastLogin: serverTimestamp(),
        email: firebaseUser.email
      }, { merge: true });

      console.log('AuthContext: User document updated');

      // Record login in audit trail
      await logAuditEvent(
        { uid: firebaseUser.uid, email: firebaseUser.email! },
        'login',
        { method: 'email_password' }
      );

    } catch (err: any) {
      console.error('AuthContext: Login error:', err);
      console.error('AuthContext: Error code:', err.code);
      console.error('AuthContext: Error message:', err.message);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError(null);
      // Record logout in audit trail before the session ends
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logAuditEvent(
          { uid: currentUser.uid, email: currentUser.email },
          'logout',
          {}
        );
      }
      await signOut(auth);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  // Create user function (admin only)
  const createUser = async (email: string, password: string, role: UserRole, displayName?: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const newUser: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        displayName,
        role,
        lastLogin: new Date(),
        createdAt: new Date()
      };
      
      console.log('createUser: Creating user with role:', role);
      console.log('createUser: New user object:', newUser);
      
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        ...newUser,
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      
          console.log('createUser: User document written to Firestore');

          console.log(`Created user with role ${role}:`, email);
      
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get all users function (admin only)
  const getAllUsers = async (): Promise<User[]> => {
    try {
      setError(null);
      const usersSnapshot = await getDocs(collection(db, 'users'));
      return usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          email: data.email,
          displayName: data.displayName,
          role: data.role,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLogin: data.lastLogin?.toDate(),
        } as User;
      });
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };



  // Clear error function
  const clearError = () => {
    setError(null);
  };

  // Debug function to check user document in Firestore
  const debugUserDocument = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      console.log('debugUserDocument: Document exists:', userDoc.exists());
      if (userDoc.exists()) {
        console.log('debugUserDocument: Document data:', userDoc.data());
      }
    } catch (err) {
      console.error('debugUserDocument: Error:', err);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('onAuthStateChanged: Firebase user:', firebaseUser?.email);
      setLoading(true);
      
      if (firebaseUser) {
        // Debug the user document in Firestore
        await debugUserDocument(firebaseUser.uid);
        
        const convertedUser = await convertFirebaseUser(firebaseUser);
        console.log('onAuthStateChanged: Converted user:', convertedUser);
        setUser(convertedUser);
      } else {
        console.log('onAuthStateChanged: No Firebase user, setting user to null');
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

      const value: AuthContextType = {
        user,
        loading,
        error,
        login,
        logout,
        createUser,
        getAllUsers,
        clearError
      };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
